import React, { useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import i18n from 'simple-react-i18n'
import moment from 'moment'
import { Alert, Box, Chip, Grid, Stack, Typography } from '@mui/material'
import { Cancel, CheckCircle, CloudQueue, WarningAmber } from '@mui/icons-material'
import { compact, isNumber } from 'lodash'
import { STATION_NAME_ASSOCIATION, STATION_TYPE_NAME } from 'station/constants/StationConstants'
import { getDate } from 'utils/DateUtil'
import { getSiteUrl } from 'utils/mapUtils/SiteTypes'
import { SmallStyledFieldSet, StyledLegend } from 'components/StyledElements'
import ProgressCard from 'components/card/ProgressCard'
import { isGenericIAModel, isLstmSupModel, isLstmSoutModel, isGr4jModel } from 'iaeau/utils/IAEauUtils'
import { CardTable } from 'components/datatable/NewTable'

const SEVERITY_SUCCESS = 'success'
const SEVERITY_WARNING = 'warning'
const SEVERITY_ERROR = 'error'

const RAIN_TYPE_ID = 1
const ETP_TYPE_ID = 3
const TEMP_MIN_CODEPOINT = 31
const TEMP_MAX_CODEPOINT = 32
const ECMWF_SOURCE = 'ecmwf'
const FORECAST_MAX_AGE_DAYS = 2
const MAX_PERMISSIBLE_DATE_GAP_DAYS = 3
const MAX_WARNING_DATE_GAP_DAYS = 10

const STATUS_PALETTES = {
    [SEVERITY_SUCCESS]: 'validStatus',
    [SEVERITY_WARNING]: 'reinforcedWarningStatus',
    [SEVERITY_ERROR]: 'errorStatus',
}

const getStatusIconSx = severity => ({ fontSize: 20, color: `${STATUS_PALETTES[severity]}.contrastText` })
const STATUS_ICONS = {
    [SEVERITY_SUCCESS]: <CheckCircle sx={getStatusIconSx(SEVERITY_SUCCESS)} />,
    [SEVERITY_WARNING]: <WarningAmber sx={getStatusIconSx(SEVERITY_WARNING)} />,
    [SEVERITY_ERROR]: <Cancel sx={getStatusIconSx(SEVERITY_ERROR)} />,
}

const getAlertSx = severity => ({
    backgroundColor: `${STATUS_PALETTES[severity]}.main`,
    color: `${STATUS_PALETTES[severity]}.contrastText`,
    border: 'solid 1px',
    borderColor: `${STATUS_PALETTES[severity]}.contrastText`,
    '& .MuiAlert-icon': { color: `${STATUS_PALETTES[severity]}.contrastText` },
})

const HEADERS_STREAMS_TABLE = ['statusIcon', 'name', 'periode', 'measures', 'status']

const hasLabel = (stat, keyword) => (stat?.label || '').toLowerCase().includes(keyword)
const hasNamePoint = (stat, keyword) => (stat?.namePoint || '').toLowerCase().includes(keyword)
const isTempStat = stat => hasLabel(stat, 'temp')

const isRainStat = stat => stat.typeId === RAIN_TYPE_ID || hasLabel(stat, 'pluie')
const isEtpStat = stat => stat.typeId === ETP_TYPE_ID || hasLabel(stat, 'etp')
const isTempMinStat = stat => isTempStat(stat) && (stat.codepoint === TEMP_MIN_CODEPOINT || hasNamePoint(stat, 'min'))
const isTempMaxStat = stat => isTempStat(stat) && (stat.codepoint === TEMP_MAX_CODEPOINT || hasNamePoint(stat, 'max'))
const isTempAvgStat = stat => isTempStat(stat) && stat.codepoint !== TEMP_MIN_CODEPOINT && stat.codepoint !== TEMP_MAX_CODEPOINT
const isRadiationStat = stat => hasLabel(stat, 'rayonnement')
const isSnowStat = stat => hasLabel(stat, 'neige')
const isSwiStat = stat => hasLabel(stat, 'humidit') || hasLabel(stat, 'swi')
const isDrainageStat = stat => hasLabel(stat, 'drain')
const isEcmwfStat = stat => (stat?.source || '').toLowerCase().includes(ECMWF_SOURCE)
const isPluvioStation = station => station.typeName === STATION_TYPE_NAME.pluviometry

const getStationLabel = station => compact([station.code, station.name]).join(' - ')

const buildStream = (id, name, stat, isAvailable = !!stat?.countTotal) => ({
    id,
    name,
    startDate: stat?.startDate,
    endDate: stat?.endDate,
    count: stat?.countTotal || 0,
    isAvailable,
    statusLabel: isAvailable ? i18n.available : i18n.missing,
    statusSeverity: isAvailable ? SEVERITY_SUCCESS : SEVERITY_ERROR,
    gapDays: null,
})

const getForecastStatus = (ecmwfStat, isUpToDate) => {
    if (!ecmwfStat) {
        return { statusLabel: i18n.missing, statusSeverity: SEVERITY_ERROR }
    }
    return isUpToDate
        ? { statusLabel: i18n.upToDate, statusSeverity: SEVERITY_SUCCESS }
        : { statusLabel: i18n.outdated, statusSeverity: SEVERITY_WARNING }
}

const evaluateStreamWithGap = (stream, maxEndMoment) => {
    if (!stream.isAvailable || !stream.endDate || !maxEndMoment) {
        const isMissing = !stream.isAvailable || !stream.count || !stream.endDate
        return {
            ...stream,
            gapDays: null,
            statusLabel: isMissing ? i18n.missing : stream.statusLabel,
            statusSeverity: isMissing ? SEVERITY_ERROR : stream.statusSeverity,
        }
    }

    const maxDay = maxEndMoment.clone().startOf('day')
    const streamDay = moment(stream.endDate).startOf('day')
    const gapDays = Math.max(0, maxDay.diff(streamDay, 'days'))

    if (gapDays <= MAX_PERMISSIBLE_DATE_GAP_DAYS) {
        return {
            ...stream,
            gapDays,
            statusLabel: stream.id === 'ecmwf_seeds' ? i18n.upToDate : i18n.available,
            statusSeverity: SEVERITY_SUCCESS,
        }
    }
    if (gapDays <= MAX_WARNING_DATE_GAP_DAYS) {
        return {
            ...stream,
            gapDays,
            statusLabel: `${i18n.dataGap} : ${gapDays} ${i18n.dayShort}`,
            statusSeverity: SEVERITY_WARNING,
        }
    }
    return {
        ...stream,
        gapDays,
        statusLabel: `${i18n.dataGap} : ${gapDays} ${i18n.dayShort}`,
        statusSeverity: SEVERITY_ERROR,
    }
}

const formatStreamRow = stream => {
    const periodLabel = stream.startDate && stream.endDate
        ? `${i18n.fromDate} ${getDate(stream.startDate)} ${i18n.to} ${getDate(stream.endDate)}`
        : '-'

    const measuresLabel = isNumber(stream.count)
        ? `${stream.count} ${i18n.measures}`
        : `${stream.count}`

    const hasGapBadge = stream.isAvailable && isNumber(stream.gapDays) && stream.gapDays > MAX_PERMISSIBLE_DATE_GAP_DAYS

    return {
        id: stream.id,
        statusIcon: (
            <Box sx={{ textAlign: 'center' }}>
                {STATUS_ICONS[stream.statusSeverity]}
            </Box>
        ),
        name: (
            <Typography
                variant='body2'
                sx={{
                    fontWeight: hasGapBadge ? 'bold' : 'normal',
                    color: stream.statusSeverity === SEVERITY_ERROR && hasGapBadge
                        ? `${STATUS_PALETTES[SEVERITY_ERROR]}.contrastText`
                        : undefined,
                }}
            >
                {stream.name}
            </Typography>
        ),
        periode: (
            <Stack direction='row' alignItems='center' spacing={1}>
                <Typography
                    variant='body2'
                    sx={{
                        color: stream.statusSeverity === SEVERITY_ERROR && hasGapBadge
                            ? `${STATUS_PALETTES[SEVERITY_ERROR]}.contrastText`
                            : undefined,
                        fontWeight: hasGapBadge ? 'bold' : 'normal',
                    }}
                >
                    {periodLabel}
                </Typography>
                {hasGapBadge && (
                    <Chip
                        size='small'
                        label={`${stream.gapDays} ${i18n.daysGapSuffix}`}
                        color={STATUS_PALETTES[stream.statusSeverity]}
                        sx={{ height: 22, fontSize: '0.75rem', fontWeight: 'bold' }}
                    />
                )}
            </Stack>
        ),
        measures: (
            <Typography variant='body2'>{measuresLabel}</Typography>
        ),
        status: (
            <Box sx={{ padding: 0.5 }}>
                <Chip
                    size='small'
                    label={stream.statusLabel}
                    color={STATUS_PALETTES[stream.statusSeverity]}
                />
            </Box>
        ),
    }
}

const StationIcon = ({ typeName }) => (
    <Box component='img' src={getSiteUrl(STATION_NAME_ASSOCIATION[typeName])} alt='' sx={{ maxHeight: 30 }} />
)

StationIcon.propTypes = {
    typeName: PropTypes.string,
}

const StreamsCardTable = ({ icon, title, subtitle, streams }) => {
    const rows = useMemo(() => streams.map(formatStreamRow), [streams])

    const formattedTitle = (
        <Stack direction='row' alignItems='center' spacing={1}>
            {icon}
            <Typography variant='h6' sx={{ fontWeight: 'bold' }}>{title}</Typography>
        </Stack>
    )

    return (
        <Grid size={12}>
            <CardTable
                title={formattedTitle}
                subTitle={subtitle}
                subtitleSize={8}
                rows={rows}
                headers={HEADERS_STREAMS_TABLE}
            />
        </Grid>
    )
}

StreamsCardTable.propTypes = {
    icon: PropTypes.element,
    title: PropTypes.string,
    subtitle: PropTypes.string,
    streams: PropTypes.arrayOf(PropTypes.shape({})),
}

const DataInputs = ({
    selectedModel = {},
    stations = [],
    stats = [],
    predStats = [],
    stationType,
    isLoading = false,
    onAvailabilityChange = () => {},
}) => {
    const isDataLoading = isLoading || (stations.length > 0 && stats.length < stations.length)
    const isGeneric = isGenericIAModel(selectedModel)
    const isLstmSup = isLstmSupModel(selectedModel)
    const isLstmSout = isLstmSoutModel(selectedModel)
    const isGr4j = isGr4jModel(selectedModel)
    const isHydro = stationType === STATION_TYPE_NAME.hydrometry

    const pluvioStation = useMemo(() => stations.find(isPluvioStation), [stations])
    const pluvioStats = useMemo(() => {
        const pluvioIndex = stations.findIndex(isPluvioStation)
        return pluvioIndex >= 0 ? (stats[pluvioIndex] ?? []) : []
    }, [stations, stats])

    const mainStation = useMemo(() => (
        stations.find(station => station.typeName === stationType) || stations[0]
    ), [stations, stationType])

    const mainStats = useMemo(() => {
        const mainIndex = Math.max(stations.findIndex(station => station.typeName === stationType), 0)
        return stats[mainIndex] ?? []
    }, [stations, stationType, stats])

    const mainTypeName = mainStation?.typeName || stationType

    const mainStreamName = useMemo(() => {
        if (isLstmSout) return i18n.ngfHeight
        return isHydro ? i18n.observedFlow : i18n.piezometricLevel
    }, [isLstmSout, isHydro])

    const rawMainStream = useMemo(() => {
        const mainStat = mainStats.find(stat => stat.typeId === selectedModel.typeId) || mainStats[0]
        return buildStream('main_station', mainStreamName, mainStat, !!(mainStat?.countTotal && mainStat?.startDate))
    }, [mainStats, selectedModel.typeId, mainStreamName])

    // Chaines 33 & 66 (Modèles génériques Scikit-Learn et CAMELS)
    const genericWeatherStreams = useMemo(() => {
        const snowStat = pluvioStats.find(isSnowStat)
        const swiStat = pluvioStats.find(isSwiStat) || (isHydro ? snowStat : undefined)
        return [
            buildStream('rain', i18n.observedRain, pluvioStats.find(isRainStat)),
            buildStream('etp', i18n.etpShort, pluvioStats.find(isEtpStat)),
            buildStream('temp_min', i18n.tempMinStream, pluvioStats.find(isTempMinStat)),
            buildStream('temp_max', i18n.tempMaxStream, pluvioStats.find(isTempMaxStat)),
            buildStream('temp_avg', i18n.tempAvgStream, pluvioStats.find(isTempAvgStat)),
            buildStream('radiation', i18n.radiationStream, pluvioStats.find(isRadiationStat)),
            buildStream('snow', i18n.snowStream, snowStat),
            buildStream('swi', i18n.swiSoilStream, swiStat),
        ]
    }, [pluvioStats, isHydro])

    // Chaine 90 & GR4J : Modèle LSTM superficiel et GR4J (Pluie, température, ETP et débits)
    const lstmSupWeatherStreams = useMemo(() => {
        const tempStat = pluvioStats.find(isTempAvgStat) || pluvioStats.find(isTempStat)
        return [
            buildStream('rain', i18n.observedRain, pluvioStats.find(isRainStat)),
            buildStream('temperature', i18n.temperature, tempStat),
            buildStream('etp', i18n.etpShort, pluvioStats.find(isEtpStat)),
        ]
    }, [pluvioStats])

    // Chaine 100 : Modèle LSTM souterrain (Pluie, Neige, SWI, drainage, ETP et hauteur NGF)
    const lstmSoutWeatherStreams = useMemo(() => {
        const snowStat = pluvioStats.find(isSnowStat)
        const swiStat = pluvioStats.find(isSwiStat)
        const drainStat = pluvioStats.find(isDrainageStat)
        return [
            buildStream('rain', i18n.observedRain, pluvioStats.find(isRainStat)),
            buildStream('snow', i18n.snowStream, snowStat),
            buildStream('swi', i18n.swiSoilStream, swiStat),
            buildStream('drainage', i18n.drainage, drainStat),
            buildStream('etp', i18n.etpShort, pluvioStats.find(isEtpStat)),
        ]
    }, [pluvioStats])

    const rawWeatherStreams = useMemo(() => {
        if (isLstmSup || isGr4j) return lstmSupWeatherStreams
        if (isLstmSout) return lstmSoutWeatherStreams
        if (isGeneric) return genericWeatherStreams
        return []
    }, [isLstmSup, isLstmSout, isGr4j, isGeneric, lstmSupWeatherStreams, lstmSoutWeatherStreams, genericWeatherStreams])

    const rawForecastStream = useMemo(() => {
        const ecmwfStat = predStats.find(isEcmwfStat)
        const lastSimulationDate = ecmwfStat?.maxSimulationDate || ecmwfStat?.maxDate
        const isUpToDate = !!lastSimulationDate && moment(lastSimulationDate).isSameOrAfter(moment().subtract(FORECAST_MAX_AGE_DAYS, 'days').startOf('day'))
        return {
            id: 'ecmwf_seeds',
            name: i18n.rainForecastEcmwf,
            startDate: ecmwfStat?.minSimulationDate || ecmwfStat?.minDate,
            endDate: lastSimulationDate,
            count: ecmwfStat?.countTotal || (lastSimulationDate ? `${i18n.simulPrefix}${getDate(lastSimulationDate)}` : 0),
            isAvailable: isUpToDate,
            ...getForecastStatus(ecmwfStat, isUpToDate),
            gapDays: null,
        }
    }, [predStats])

    const hasWeatherStreams = isGeneric || isLstmSup || isLstmSout || isGr4j
    const hasForecastStream = isGeneric

    const rawAllStreams = useMemo(() => {
        if (hasForecastStream) {
            return [rawMainStream, ...rawWeatherStreams, rawForecastStream]
        }
        if (hasWeatherStreams) {
            return [rawMainStream, ...rawWeatherStreams]
        }
        return [rawMainStream]
    }, [hasForecastStream, hasWeatherStreams, rawMainStream, rawWeatherStreams, rawForecastStream])

    const overallMaxEndDate = useMemo(() => {
        const streamsWithDate = rawAllStreams.filter(stream => stream.isAvailable && !!stream.endDate)
        if (!streamsWithDate.length) return null
        return moment.max(streamsWithDate.map(stream => moment(stream.endDate)))
    }, [rawAllStreams])

    const evaluatedMainStream = useMemo(() => (
        evaluateStreamWithGap(rawMainStream, overallMaxEndDate)
    ), [rawMainStream, overallMaxEndDate])

    const evaluatedWeatherStreams = useMemo(() => (
        rawWeatherStreams.map(stream => evaluateStreamWithGap(stream, overallMaxEndDate))
    ), [rawWeatherStreams, overallMaxEndDate])

    const evaluatedForecastStream = useMemo(() => (
        evaluateStreamWithGap(rawForecastStream, overallMaxEndDate)
    ), [rawForecastStream, overallMaxEndDate])

    const evaluatedAllStreams = useMemo(() => {
        if (hasForecastStream) {
            return [evaluatedMainStream, ...evaluatedWeatherStreams, evaluatedForecastStream]
        }
        if (hasWeatherStreams) {
            return [evaluatedMainStream, ...evaluatedWeatherStreams]
        }
        return [evaluatedMainStream]
    }, [hasForecastStream, hasWeatherStreams, evaluatedMainStream, evaluatedWeatherStreams, evaluatedForecastStream])

    const missingStreams = useMemo(() => (
        evaluatedAllStreams.filter(stream => !stream.isAvailable || !stream.count || !stream.endDate)
    ), [evaluatedAllStreams])

    const excessiveGapStreams = useMemo(() => (
        evaluatedAllStreams.filter(stream => stream.isAvailable && isNumber(stream.gapDays) && stream.gapDays > MAX_WARNING_DATE_GAP_DAYS)
    ), [evaluatedAllStreams])

    const warningGapStreams = useMemo(() => (
        evaluatedAllStreams.filter(stream => (
            stream.isAvailable &&
            isNumber(stream.gapDays) &&
            stream.gapDays > MAX_PERMISSIBLE_DATE_GAP_DAYS &&
            stream.gapDays <= MAX_WARNING_DATE_GAP_DAYS
        ))
    ), [evaluatedAllStreams])

    const blockingItems = useMemo(() => (
        [...missingStreams, ...excessiveGapStreams]
    ), [missingStreams, excessiveGapStreams])

    const hasBlockingError = !!blockingItems.length
    const hasWarning = !hasBlockingError && !!warningGapStreams.length
    const alertSeverity = hasBlockingError ? SEVERITY_ERROR : (hasWarning ? SEVERITY_WARNING : SEVERITY_SUCCESS)

    const alertTitle = useMemo(() => {
        if (hasBlockingError) {
            if (missingStreams.length && !excessiveGapStreams.length) return i18n.someRequiredDataMissing
            if (!missingStreams.length && excessiveGapStreams.length) return i18n.dataGapTooLarge
            return i18n.someRequiredDataMissing
        }
        if (hasWarning) {
            return i18n.dataGapWarning
        }
        return i18n.allRequiredDataAvailable
    }, [hasBlockingError, hasWarning, missingStreams.length, excessiveGapStreams.length])

    const blockingKey = blockingItems.map(stream => `${stream.id}_${stream.gapDays}`).join(',')
    const warningKey = warningGapStreams.map(stream => `${stream.id}_${stream.gapDays}`).join(',')

    useEffect(() => {
        if (isDataLoading) return
        onAvailabilityChange({
            hasBlockingError,
            hasWarning,
            blockingItems,
            warningItems: warningGapStreams,
            missingItems: blockingItems,
            streams: evaluatedAllStreams,
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isDataLoading, blockingKey, warningKey, onAvailabilityChange])

    if (isDataLoading) {
        return (
            <SmallStyledFieldSet>
                <StyledLegend>{i18n.datas}</StyledLegend>
                <ProgressCard indeterminate />
            </SmallStyledFieldSet>
        )
    }

    return (
        <SmallStyledFieldSet>
            <StyledLegend>{i18n.datas}</StyledLegend>
            <Grid container spacing={2}>
                <Grid size={12}>
                    <Alert severity={alertSeverity} sx={getAlertSx(alertSeverity)}>
                        <Typography variant='subtitle2' sx={{ fontWeight: 'bold' }}>
                            {alertTitle}
                        </Typography>
                        {!!missingStreams.length && (
                            <Typography variant='body2' sx={{ marginTop: 0.5 }}>
                                {`${i18n.missingDataPrefix}${missingStreams.map(stream => stream.name).join(', ')}`}
                            </Typography>
                        )}
                        {!!excessiveGapStreams.length && (
                            <Typography variant='body2' sx={{ marginTop: 0.5 }}>
                                {`${i18n.dataGapStreamsPrefix}${excessiveGapStreams.map(stream => `${stream.name} (${stream.gapDays} ${i18n.daysGapSuffix})`).join(', ')}`}
                            </Typography>
                        )}
                        {hasWarning && !!warningGapStreams.length && (
                            <Typography variant='body2' sx={{ marginTop: 0.5 }}>
                                {`${i18n.dataGapStreamsPrefix}${warningGapStreams.map(stream => `${stream.name} (${stream.gapDays} ${i18n.daysGapSuffix})`).join(', ')}`}
                            </Typography>
                        )}
                    </Alert>
                </Grid>
                {!!mainStation && (
                    <StreamsCardTable
                        icon={<StationIcon typeName={mainTypeName} />}
                        title={i18n[mainTypeName]}
                        subtitle={getStationLabel(mainStation)}
                        streams={[evaluatedMainStream]}
                    />
                )}
                {hasWeatherStreams && !!pluvioStation && (
                    <StreamsCardTable
                        icon={<StationIcon typeName={STATION_TYPE_NAME.pluviometry} />}
                        title={i18n[STATION_TYPE_NAME.pluviometry]}
                        subtitle={getStationLabel(pluvioStation)}
                        streams={evaluatedWeatherStreams}
                    />
                )}
                {hasForecastStream && (
                    <StreamsCardTable
                        icon={<CloudQueue color='primary' sx={{ fontSize: 28 }} />}
                        title={i18n.weatherForecastCategory}
                        subtitle={pluvioStation ? getStationLabel(pluvioStation) : i18n.meteoAreaStation}
                        streams={[evaluatedForecastStream]}
                    />
                )}
            </Grid>
        </SmallStyledFieldSet>
    )
}

DataInputs.propTypes = {
    selectedModel: PropTypes.shape({
        idScenario: PropTypes.number,
        typeModel: PropTypes.string,
        typeId: PropTypes.number,
        chainId: PropTypes.number,
        chain: PropTypes.number,
        idChain: PropTypes.number,
    }),
    stations: PropTypes.arrayOf(PropTypes.shape({})),
    stats: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.shape({}))),
    predStats: PropTypes.arrayOf(PropTypes.shape({})),
    stationType: PropTypes.string,
    isLoading: PropTypes.bool,
    onAvailabilityChange: PropTypes.func,
}

export default DataInputs
